import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import Card from 'app/frontend/base/components/Card'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import type OccupancyStatus from 'app/frontend/types/custom/OccupancyStatus'
import {
  Check,
  ChevronLeft,
  ChevronRight,
  FileImage,
  Heart,
  MessageCircleMore,
  Lock,
  Undo2,
  Video,
  X,
} from 'lucide-react'
import { useState } from 'react'
import type { MapContent, MapPOI } from '../utils'
import EditNoteModal from './EditNoteModal'
import {
  choiceLabels,
  plotImageUrl,
  rankedChoiceIconClass,
  statusClassName,
} from './housingUi'
import PlotMinimap from './PlotMinimap'
import type { DisplayOccupant } from './types'
import { cn } from '@/lib/utils'

type Props = {
  plot: MapPOI | null
  plots: MapPOI[]
  neighborhood: MapContent
  mapId: number
  videoUrl: string
  plotOccupants: DisplayOccupant[]
  currentUserChoices: DisplayOccupant[]
  selectedPlotUserChoice?: DisplayOccupant
  shardIndex: number
  isAdmin: boolean
  userLoggedIn: boolean
  mutationPending: boolean
  userHasApprovedPlot: boolean
  onClose: () => void
  onOpenPlot: (plot: MapPOI | null) => void
  onRequest: (rankedChoice: number, plotId?: string | number) => void
  onWithdraw: (rankedChoice: number) => void
  onSaveNote: (occupantId: number, note: string) => void
  onStatusChange: (occupantId: number, status: OccupancyStatus) => void
}

const PlotModal = ({
  plot,
  plots,
  neighborhood,
  mapId,
  videoUrl,
  plotOccupants,
  currentUserChoices,
  selectedPlotUserChoice,
  shardIndex,
  isAdmin,
  userLoggedIn,
  mutationPending,
  userHasApprovedPlot,
  onClose,
  onOpenPlot,
  onRequest,
  onWithdraw,
  onSaveNote,
  onStatusChange,
}: Props) => {
  const [editingNote, setEditingNote] = useState(false)
  const plotIndex = plots.findIndex(
    (targetPlot) => targetPlot.Name === plot?.Name,
  )
  const previousPlot = plotIndex > 0 ? plots[plotIndex - 1] : undefined
  const nextPlot =
    plotIndex >= 0 && plotIndex < plots.length - 1
      ? plots[plotIndex + 1]
      : undefined
  const claimedOccupant = plotOccupants.find(
    (occupant) => occupant.status === 'claimed',
  )
  const sortedOccupants = plotOccupants
    .filter(
      (occupant) =>
        occupant.status !== 'claimed' &&
        (occupant.status === 'approved' ||
          !occupant.character.userHasApprovedPlot) &&
        !occupant.character.userHasClaimedPlot &&
        occupant.shard_index === shardIndex,
    )
    .sort((a, b) => a.character.name.localeCompare(b.character.name))
    .sort((a, b) => a.ranked_choice - b.ranked_choice)
  const primaryOccupant = sortedOccupants.find(
    (occupant) =>
      occupant.status === 'approved' &&
      occupant.id !== selectedPlotUserChoice?.id,
  )

  return (
    <Dialog
      open={!!plot}
      onOpenChange={(open) => {
        if (!open) {
          setEditingNote(false)
          onClose()
        }
      }}
    >
      <DialogContent className='w-fit max-w-[calc(100vw-2rem)]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-5'>
            <span>Plot {plot?.Name}</span>
            {plot ? (
              <span className='flex items-center gap-3 [&_a:hover]:text-foreground [&_a]:text-muted-foreground'>
                <a
                  href={`${videoUrl}&t=${
                    plot.VideoTimestamp ?? 0
                  }s&mute=1&autoplay=1`}
                  target='_blank'
                  rel='noreferrer'
                >
                  <Video className='h-4 w-4' />
                </a>
                <a
                  href={plotImageUrl(mapId, plot.Name)}
                  target='_blank'
                  rel='noreferrer'
                >
                  <FileImage className='h-4 w-4' />
                </a>
              </span>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        {plot ? (
          <section className='flex min-h-[25rem] max-w-[calc(100vw-5rem)] gap-4'>
            <Button
              variant='ghost'
              size='icon'
              disabled={!previousPlot}
              className={`ml-[-0.625rem] mt-[12rem] bg-transparent hover:bg-transparent ${
                previousPlot ? '' : 'pointer-events-none opacity-0'
              }`}
              onClick={() => onOpenPlot(previousPlot ?? null)}
            >
              <ChevronLeft className='h-6 w-6' />
            </Button>

            <div className='flex min-w-0 flex-col gap-4'>
              <div>
                <img
                  src={plotImageUrl(mapId, plot.Name)}
                  alt={`Plot ${plot.Name}`}
                  className='w-full max-w-[calc(100vw-5rem)] select-none rounded-md object-cover'
                />
              </div>

              <div className='flex gap-4'>
                <PlotMinimap
                  mapId={mapId}
                  neighborhood={neighborhood}
                  plot={plot}
                />

                <div className='flex min-w-[24rem] grow flex-col gap-3'>
                  {claimedOccupant ? (
                    <Alert variant='info' className='py-2'>
                      <AlertDescription className='flex items-center gap-2'>
                        <Lock className='h-4 w-4 text-info-foreground' />
                        Claimed by {claimedOccupant.character.name}
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  <Card className='py-0'>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className='py-1 px-1'>Occupant</TableHead>
                          <TableHead className='py-1 px-1'>Status</TableHead>
                          <TableHead className='py-1 px-1'>Choice</TableHead>
                          {isAdmin ? <TableHead className='py-1 px-1' /> : null}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedOccupants.map((occupant) => (
                          <TableRow key={occupant.id}>
                            <TableCell className='py-1 px-1'>
                              <span
                                className={
                                  occupant.character.characterClass
                                    ? classColours[
                                        occupant.character.characterClass
                                      ].text
                                    : ''
                                }
                              >
                                {occupant.character.name}
                              </span>
                            </TableCell>
                            <TableCell className='py-1 px-1'>
                              <div className='flex items-center gap-2'>
                                <span
                                  className={cn(
                                    statusClassName[occupant.status],
                                    'text-xs',
                                  )}
                                >
                                  {occupant.status}
                                </span>
                                {occupant.note ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <MessageCircleMore className='h-4 w-4 text-muted-foreground' />
                                    </TooltipTrigger>
                                    <TooltipContent className='max-w-80 whitespace-pre-wrap'>
                                      {occupant.note}
                                    </TooltipContent>
                                  </Tooltip>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className='py-1 px-1'>
                              <span className='flex items-center gap-1'>
                                <Heart
                                  className={`h-4 w-4 ${rankedChoiceIconClass(
                                    occupant.ranked_choice,
                                  )}`}
                                />
                                {choiceLabels[occupant.ranked_choice]}
                              </span>
                            </TableCell>
                            {isAdmin ? (
                              <TableCell className='py-1 px-1'>
                                <div className='flex gap-1'>
                                  {occupant.status === 'requested' ? (
                                    <>
                                      <Button
                                        size='xs'
                                        variant='ghost'
                                        className='px-1'
                                        disabled={mutationPending}
                                        onClick={() =>
                                          onStatusChange(
                                            occupant.id,
                                            'approved',
                                          )
                                        }
                                      >
                                        <Check className='h-4 w-4 text-success-foreground' />
                                      </Button>
                                      <Button
                                        size='xs'
                                        variant='ghost'
                                        className='px-1'
                                        disabled={mutationPending}
                                        onClick={() =>
                                          onStatusChange(
                                            occupant.id,
                                            'rejected',
                                          )
                                        }
                                      >
                                        <X className='h-4 w-4 text-destructive-foreground' />
                                      </Button>
                                    </>
                                  ) : (
                                    <Button
                                      size='xs'
                                      variant='ghost'
                                      className='px-1'
                                      disabled={mutationPending}
                                      onClick={() =>
                                        onStatusChange(occupant.id, 'requested')
                                      }
                                    >
                                      <Undo2 className='h-4 w-4' />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            ) : null}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {sortedOccupants.length === 0 ? (
                      <span className='flex justify-center text-sm text-muted-foreground py-2'>
                        {claimedOccupant
                          ? 'This plot is claimed'
                          : 'This plot is still available'}
                      </span>
                    ) : null}
                  </Card>
                </div>
              </div>

              {userLoggedIn ? (
                <div className='grid grid-cols-[1fr_1fr_1fr_2.5rem] items-end gap-2 max-[570px]:flex max-[570px]:flex-col [&_button]:flex [&_button]:items-center [&_button]:justify-center'>
                  {[1, 2, 3].map((rankedChoice) => {
                    const choice = currentUserChoices.find(
                      (occupant) => occupant.ranked_choice === rankedChoice,
                    )
                    const selectedHere =
                      choice?.plot_id.toString() === plot.Name

                    return (
                      <Button
                        key={rankedChoice}
                        variant={selectedHere ? 'default' : 'outline'}
                        disabled={
                          mutationPending ||
                          (!selectedHere &&
                          (primaryOccupant || userHasApprovedPlot)
                            ? true
                            : false)
                        }
                        onClick={() =>
                          selectedHere
                            ? onWithdraw(rankedChoice)
                            : onRequest(rankedChoice, plot.Name)
                        }
                      >
                        {selectedHere
                          ? 'Withdraw'
                          : `${choiceLabels[rankedChoice]} choice`}
                      </Button>
                    )
                  })}

                  <Button
                    variant='outline'
                    size='icon'
                    disabled={!selectedPlotUserChoice || mutationPending}
                    onClick={() => setEditingNote(true)}
                  >
                    <MessageCircleMore className='h-4 w-4' />
                  </Button>
                  <EditNoteModal
                    occupant={
                      editingNote ? selectedPlotUserChoice ?? null : null
                    }
                    mutationPending={mutationPending}
                    onClose={() => setEditingNote(false)}
                    onSave={onSaveNote}
                  />
                </div>
              ) : null}
            </div>

            <Button
              variant='ghost'
              size='icon'
              disabled={!nextPlot}
              className={`mr-[-0.625rem] mt-[12rem] bg-transparent hover:bg-transparent ${
                nextPlot ? '' : 'pointer-events-none opacity-0'
              }`}
              onClick={() => onOpenPlot(nextPlot ?? null)}
            >
              <ChevronRight className='h-6 w-6' />
            </Button>
          </section>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export default PlotModal
